import React, { useEffect } from "react";
import { Spinner, AnchorButton, Button, Collapse, Icon } from "@blueprintjs/core";
import customTableComponents from "./OrderListTableComponents";
import classnames from "classnames";
import styles from "./OrderList.module.scss";
import {
  useTable,
  useSortBy,
  useFlexLayout,
  useResizeColumns,
  useExpanded
} from "react-table";

const OrderList = ({
  isLoading,
  loadingMore,
  orders,
  columns,
  onSortPress,
  onStatusChange,
  emptyMessage = "No Orders Found",
  sort
}) => {
  const CellRenderer = customTableComponents({ onStatusChange });
  const {
    state: { sortBy },
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable(
    {
      columns,
      data: orders,
      manualSortBy: true,
      autoResetSortBy: true
    },
    useSortBy,
    useResizeColumns,
    useFlexLayout,
    useExpanded
  );
  useEffect(() => {
    if (sortBy[0]) {
      const { id, desc } = sortBy[0];
      onSortPress(id, desc ? "desc" : "asc");
    }
  }, [sortBy]);

  return (
    <div className={styles.tableWrapper}>
      <div {...getTableProps()} className={styles.table}>
        <div className={styles.headerRow}>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className={styles.tr}>
              {headerGroup.headers.map((column) => (
                <div
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={styles.th}
                >
                  {column.render("Header")}
                  {/* Use column.getResizerProps to hook up the events correctly */}
                  <span>
                    {column.id == sort.field
                      ? sort.direction == "desc"
                        ? " ↓"
                        : " ↑"
                      : ""}
                  </span>
                  {column.canResize && (
                    <div
                      {...column.getResizerProps()}
                      className={classnames(
                        styles.resizer,
                        column.isResizing && styles.isResizing
                      )}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div {...getTableBodyProps()}>
          {!isLoading && rows.length === 0 && (
            <div className={styles.empty}>{emptyMessage}</div>
          )}
          {isLoading && (
            <div className={styles.empty}>
              <Spinner />
            </div>
          )}
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <>
                <div
                  {...row.getRowProps()}
                  className={classnames(
                    styles.tr,
                    row.isExpanded && styles.expanded
                  )}
                >
                  {row.cells.map((cell, index) => {
                    if (cell.column.id == "expander") {
                      return (
                        <div {...cell.getCellProps()} className={styles.td}>
                          <span
                            className={classnames(
                              styles.cellText,
                              styles.btnViewDetail
                            )}
                            {...row.getToggleRowExpandedProps()}
                          >
                            <Icon icon="eye-open" /> View Details
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div {...cell.getCellProps()} className={styles.td}>
                        <CellRenderer cell={cell} className={styles.cellText} />
                      </div>
                    );
                  })}
                </div>
                <Collapse isOpen={row.isExpanded}>
                  <div className={styles.detailsContainer}>
                    <div className={styles.detailsRow}>
                      <div>
                        <div>
                          <h5>Actions</h5>
                        </div>
                        <div className={styles.actionToolbar}>
                          <AnchorButton target="_blank" href={`${process.env.REACT_APP_WEB_URL}/order/${row.original.id}?token=${row.original.viewingToken}`}>
                            Open Receipt
                          </AnchorButton>
                          <AnchorButton href={`mailto:${row.original.email}`}>
                            Email Customer
                          </AnchorButton>
                        </div>
                      </div>
                    </div>
                    <div className={styles.detailsRow}>
                      <div
                        className={styles.closeButton}
                        {...row.getToggleRowExpandedProps()}
                      >
                        <Icon icon="cross" />
                      </div>
                      <div className={styles.detailsColumn}>
                        <h5>Sold By</h5>
                        <p>{row.original.seller}</p>
                      </div>
                      <div className={styles.detailsColumn}>
                        <h5>Purchased By</h5>
                        <p>{row.original.buyer}</p>
                      </div>
                      <div className={styles.detailsColumn}>
                        <h5>Email</h5>
                        <p>{row.original.email}</p>
                      </div>
                      <div className={styles.detailsColumn}>
                        <h5>Address</h5>
                        <p>{row.original.address}</p>
                      </div>
                    </div>
                    {row.original.employer && (
                      <div className={styles.detailsRow}>
                        <div
                          className={classnames(
                            styles.detailsColumn,
                            styles.politicalCheckout
                          )}
                        >
                          <h4>Political Checkout</h4>
                        </div>
                        <div className={styles.detailsColumn}>
                          <h5>Employer</h5>
                          <p>{row.original.employer}</p>
                        </div>
                        <div className={styles.detailsColumn}>
                          <h5>Occupation</h5>
                          <p>
                            {row.original.occupation !== "Retired"
                              ? row.original.occupation
                              : "N/A"}
                          </p>
                        </div>
                        <div className={styles.detailsColumn}>
                          <h5>Employer Address</h5>
                          <p>{row.original.employerAddress || "N/A"}</p>
                        </div>
                      </div>
                    )}
                    <div className={styles.detailsRow}>
                      <div className={styles.orderNote}>
                        <h5>Order Note</h5>
                        <p>{row.original.customerNote || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrderList;
